import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ChevronRight, ArrowRight, ArrowLeft, ChevronDown } from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import Seo, {
  buildFaqSchema,
  buildBreadcrumbSchema,
  buildArticleSchema,
  ORGANIZATION_SCHEMA,
  SITE_URL,
} from "@/components/Seo";
import { usePersona } from "@/contexts/PersonaContext";
import { trackEvent } from "@/lib/analytics";
import { useBooking } from "@/contexts/BookingContext";
import { EditorialText } from "@/components/PillarEditorial";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COVER_BY_COUNTRY = {
  Spain: "/img/spain-cover.jpg",
};

export default function SeoLanding({ slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const { open: openBooking } = useBooking();
  const { updatePersona } = usePersona();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setPage(null);
    setNotFound(false);
    axios
      .get(`${API}/seo-pages/${slug}`)
      .then((r) => {
        if (!active) return;
        setPage(r.data);
        // Reflect the page intent into the global persona so home/booking match
        if (r.data?.intent && r.data?.country) {
          updatePersona({ persona: r.data.intent, country: r.data.country });
        }
        trackEvent("seo_page_view", {
          slug: r.data?.slug,
          country: r.data?.country,
          intent: r.data?.intent,
        });
      })
      .catch(() => active && setNotFound(true));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (notFound) {
    return (
      <main className="min-h-screen bg-[#F5EFE7] text-[#451C25]">
        <Seo title="Page not found | Destino Europe" description="This page is not available." path={window.location.pathname} noindex />
        <Header />
        <div className="max-w-3xl mx-auto pt-40 pb-20 px-6 text-center">
          <h1 className="font-display text-4xl">Page not found</h1>
          <p className="mt-4 text-[#6B4954]">This page isn't available. Try our home page or Resources.</p>
          <Link to="/" className="mt-6 inline-block text-[#451C25] underline underline-offset-4">
            Back home
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  if (!page) {
    return (
      <main className="min-h-screen bg-[#F5EFE7]">
        <Header />
        <div className="pt-40 pb-20 px-6 max-w-5xl mx-auto">
          <div className="h-10 w-2/3 bg-[#451C25]/5 mb-4 animate-pulse" />
          <div className="h-32 w-full bg-[#451C25]/5 animate-pulse" />
        </div>
      </main>
    );
  }

  const cover = page.og_image || COVER_BY_COUNTRY[page.country];
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goBookConsultation = () => {
    trackEvent("schedule_call_click", { placement: `seo_${page.slug}`, mode: "embedded" });
    openBooking(`seo_${page.slug}`);
  };

  const editorialGuide = page.slug === "nie-number-americans-spain";
  const parentPath = "/spain/moving-to-spain/";
  const breadcrumbs = editorialGuide ? [
    { name: "Home", url: `${SITE_URL}/` },
    { name: "Moving to Spain", url: `${SITE_URL}${parentPath}` },
    { name: "NIE number", url: `${SITE_URL}/${page.slug}` },
  ] : [
    { name: "Home", url: SITE_URL },
    {
      name: page.intent === "retire" ? "Retire abroad" : "Relocate abroad",
      url:
        typeof window !== "undefined"
          ? `${SITE_URL}/${page.intent === "retire" ? "retire" : "relocate"}`
          : "",
    },
    {
      name: page.country,
      url: typeof window !== "undefined" ? window.location.href : "",
    },
  ];

  return (
    <main data-testid="seo-landing" className="min-h-screen bg-[#F5EFE7] text-[#451C25]">
      <Seo
        title={page.meta_title}
        description={page.meta_description}
        image={cover}
        path={`/${page.slug}`}
        type="article"
        jsonLd={[
          ORGANIZATION_SCHEMA,
          buildBreadcrumbSchema(breadcrumbs),
          ...(editorialGuide ? [buildArticleSchema({
            title: page.h1, description: page.meta_description,
            url: `${SITE_URL}/${page.slug}`, image: cover,
            published: page.created_at, modified: page.updated_at,
          }), buildFaqSchema(page.faq || [])] : [buildFaqSchema(page.faq || [])]),
        ]}
      />

      <Header />

      <section className={`relative ${editorialGuide ? "pt-44 md:pt-44" : "pt-32 md:pt-40"} pb-12 md:pb-20 px-6 md:px-10 lg:px-14 overflow-hidden`}>
        {cover && (
          <>
            <img
              src={cover}
              alt={page.country}
              loading="eager"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(42,46,38,0.55) 0%, rgba(42,46,38,0.86) 100%)",
              }}
            />
          </>
        )}
        <div className="relative max-w-5xl mx-auto text-white">
          <nav
            data-testid="seo-breadcrumb"
            aria-label="Breadcrumb"
            className="text-[11px] tracking-[0.18em] uppercase text-white/70 mb-6 flex flex-wrap items-center gap-2"
          >
            <Link to="/" data-testid="seo-breadcrumb-home" className="hover:text-white">Home</Link>
            <ChevronRight size={12} />
            <Link
              to={editorialGuide ? parentPath : page.intent === "retire" ? "/retire" : "/relocate"}
              data-testid="seo-breadcrumb-parent"
              className="hover:text-white"
            >
              {editorialGuide ? "Moving to Spain" : page.intent === "retire" ? "Retire abroad" : "Relocate abroad"}
            </Link>
            <ChevronRight size={12} />
            <span data-testid="seo-breadcrumb-current" className="text-white">{editorialGuide ? "NIE number" : page.country}</span>
          </nav>
          <h1
            data-testid="seo-h1"
            className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02]"
          >
            {page.h1}
          </h1>
          <p className="mt-6 max-w-3xl text-white/85 text-base md:text-lg leading-relaxed font-light">
            {page.sub}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <button
              data-testid="seo-cta-book"
              onClick={goBookConsultation}
              className="inline-flex items-center justify-center gap-3 bg-[#451C25] text-white px-9 py-4 text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-[#2B1017] transition-colors"
            >
              Schedule a Free Call
              <ArrowRight size={14} />
            </button>
            <button
              data-testid="seo-jump-faq"
              onClick={() => scrollTo("seo-faq")}
              className="inline-flex items-center justify-center gap-3 border border-white/60 text-white px-9 py-4 text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-white hover:text-[#451C25] transition-colors"
            >
              Jump to FAQ
            </button>
          </div>
        </div>
      </section>

      <article className="px-6 md:px-10 lg:px-14 py-16 md:py-24">
        <div className="max-w-3xl mx-auto space-y-12">
          {page.sections.map((s, i) => (
            <section key={i} data-testid={`seo-section-${i}`}>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#451C25] mb-4">
                {s.h2}
              </h2>
              {editorialGuide ? s.body.split(/\n\n+/).map((text, j) => (
                <p key={j} className="text-[#451C25] text-[17px] leading-[1.8] font-light mb-4">
                  <EditorialText text={text} testId={`seo-section-${i}-paragraph-${j}`} />
                </p>
              )) : <p className="text-[#451C25] text-[17px] leading-[1.8] font-light">{s.body}</p>}
            </section>
          ))}
        </div>
      </article>

      {editorialGuide && <aside data-testid="seo-editorial-review" className="px-6 md:px-10 lg:px-14 pb-16">
        <div className="max-w-3xl mx-auto text-sm text-[#6B4954] leading-relaxed">
          <h2 className="font-serif text-lg text-[#451C25]">Sources and review</h2>
          <p data-testid="seo-review-date" className="mt-3">Last reviewed: <time dateTime={page.last_reviewed}>{page.last_reviewed}</time>. General information, not legal or immigration advice. Confirm current requirements with the competent authority.</p>
          <p data-testid="seo-publication-dates" className="mt-2">By Destino Europe · Published {page.created_at?.slice(0, 10)} · Updated {page.updated_at?.slice(0, 10)}</p>
          <ul className="mt-4 space-y-3">
            {(page.sources || []).map((source, i) => <li key={source}><a data-testid={`seo-official-source-${i}`} href={source} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 break-words hover:text-[#451C25] transition-colors">{["National Police: NIE assignment and documents", "Ministry of the Interior: NIE identification and representation", "Spanish consulate: NIE applications from the U.S.", "National Police: current fees"][i] || new URL(source).hostname}</a></li>)}
          </ul>
        </div>
      </aside>}

      {page.faq?.length > 0 && (
        <section
          id="seo-faq"
          data-testid="seo-faq"
          className="bg-[#EDE4D6] px-6 md:px-10 lg:px-14 py-20 md:py-28"
        >
          <div className="max-w-3xl mx-auto">
            <p className="text-[11px] tracking-[0.32em] uppercase text-[#6B4954] mb-5">
              FAQ · {page.country}
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.04] text-[#451C25] mb-10">
              The questions we always hear.
            </h2>
            <div className="divide-y divide-[#451C25]/15 border-y border-[#451C25]/15">
              {page.faq.map((f, i) => (
                <FaqItem key={i} index={i} q={f.q} a={f.a} />
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#451C25] text-white px-6 md:px-10 lg:px-14 py-20 text-center">
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-5">
          Ready to talk about your {page.country} move?
        </h2>
        <p className="text-white/80 max-w-xl mx-auto mb-8">
          A free call with Karin or the team, with no obligation. We'll talk through where you are and what would help.
        </p>
        <button
          data-testid="seo-cta-bottom"
          onClick={goBookConsultation}
          className="inline-flex items-center justify-center gap-3 bg-[#451C25] text-white px-9 py-4 text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-[#2B1017] transition-colors"
        >
          Schedule a Free Call
          <ArrowRight size={14} />
        </button>
        <div className="mt-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white text-[11px] tracking-[0.22em] uppercase"
          >
            <ArrowLeft size={14} /> Back to all countries
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}

function FaqItem({ q, a, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-5">
      <button
        data-testid={`seo-faq-q-${index}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-6 text-left"
      >
        <span className="font-serif text-lg md:text-xl text-[#451C25]">{q}</span>
        <ChevronDown
          size={20}
          className={`mt-1 shrink-0 text-[#6B4954] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <p className="mt-3 text-[#6B4954] text-[15px] leading-relaxed font-light">{a}</p>
      )}
    </div>
  );
}
